import { duplicateService, startingService, starterTypes, tradeNames, type ServiceConfiguration, type ServiceTemplate } from '../../shared/proposal-services';
import { ServiceEditor } from '../../components/proposals/ServiceEditor';
import './dealProposals.css';
import ProposalNumberEditor from '../../components/proposals/ProposalNumberEditor';
import PresentationSettingsEditor from '../../components/proposals/PresentationSettingsEditor';
import ProposalPhotoSelection from '../../components/proposals/ProposalPhotoSelection';
import { useProposalPdf } from '../../hooks/useProposalPdf';
import { proposalDealsApi, dealError, type DealType } from '../../lib/proposalDealsApi';
/**
 * ProposalBuilder - SumoQuote-style proposal editor
 * Left panel: Toggleable pages
 * Right panel: Page configuration / Tier editor
 */

import React, { useState, useEffect, useRef, useSyncExternalStore } from 'react';
import { useParams, useNavigate, useSearchParams, useBlocker } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, useIsMutating } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Input,
  Textarea,
  Autocomplete,
  FormControl,
  FormLabel,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Switch,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Select,
  Option,
  Drawer,
} from '@mui/joy';
import {
  ArrowLeft as ArrowBack,
  FloppyDisk as Save,
  PaperPlaneTilt as Send,
  FilePdf as PictureAsPdf,
  Eye as Visibility,
  User as Person,
  House as Home,
  Buildings as Business,
  Wrench as Build,
  Plus as Add,
  Trash as Delete,
  CloudArrowUp as CloudUpload,
  Image as ImageIcon,
  ListBullets as PagesIcon,
  ShieldCheck,
  CheckCircle,
} from '@phosphor-icons/react';

import { proposalsApi } from '../../lib/proposalsApi';
import { apiClient } from '../../lib/api';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useProposalDraft } from '../../hooks/useProposalDraft';
import { pendingDrafts, subscribeDrafts, flushDrafts } from '../../lib/proposalDraftRegistry';
import { DebouncedInput, DebouncedTextarea } from '../../components/common/DebouncedInput';
import ProposalBuilderTools from './ProposalBuilderTools';
import { PageList } from '../../components/proposals/PageList';
import { OptionalUpgradesEditor } from '../../components/proposals/OptionalUpgradesEditor';
import { TierEditor } from '../../components/proposals/TierEditor';
import { DEFAULT_TIER_LABELS } from '../../types/proposals';
import type { ProposalDetail, ProposalLineItem, ProposalPage, ProposalType } from '../../types/proposals';
import type { DetailedMeasurement } from '../../hooks/measurements/useDetailedMeasurements';

const PROPOSAL_TYPES: { value: ProposalType; label: string; icon: React.ReactNode; description: string }[] = [
  { value: 'residential', label: 'Residential', icon: <Home />, description: 'Residential roofing projects' },
  { value: 'commercial', label: 'Commercial', icon: <Business />, description: 'Commercial roofing projects' },
  { value: 'other_projects', label: 'Other Projects', icon: <Build />, description: 'Siding, windows, gutters, etc.' },
];

// Round UP to nearest whole number, .33, or .66 (never round down)
const roundUpToThird = (value: number): number => {
  const whole = Math.floor(value);
  const decimal = value - whole;

  if (decimal === 0) {
    return whole;
  } else if (decimal <= 0.33) {
    return whole + 0.33;
  } else if (decimal <= 0.66) {
    return whole + 0.66;
  } else {
    return whole + 1;
  }
};

export default function ProposalBuilder() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isNew = !id;
  const opportunityId = searchParams.get('opportunity');
  const [dealType, setDealType] = useState<'retail' | DealType>(() => {
    const value = searchParams.get('deal_type');
    return value === 'insurance' || value === 'repair' ? value : 'retail';
  });
  const dealRequest = useRef(crypto.randomUUID());
  const dealSettings = useQuery({queryKey:['proposal-deal-settings'],queryFn:proposalDealsApi.settings,enabled:isNew});
  const createDeal = useMutation({
    mutationFn: (data:{opportunity:number;deal_type:DealType;proposal_type:string}) => proposalDealsApi.create({...data,request_id:dealRequest.current}),
    onSuccess: result => { void queryClient.invalidateQueries({queryKey:['proposal-deals']}); navigate(`/crm/proposals/deals/${result.id}`,{replace:true}); },
    onError: error => toast.error(dealError(error)),
  });

  const [selectedPageId, setSelectedPageId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<number | null>(searchParams.get('tab') === 'services' ? 5 : null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [measurementContext, setMeasurementContext] = useState<Record<string, unknown>>({});
  const manualMeasurement = useRef(false);
  const activeEdits = useIsMutating();
  const routeId = useRef(id); routeId.current = id;
  const childDraftCount = useSyncExternalStore(subscribeDrafts, pendingDrafts, () => 0);
  const [selectedProposalType, setSelectedProposalType] = useState<ProposalType>('residential');
  const [pagesDrawerOpen, setPagesDrawerOpen] = useState(false);
  const isMobile = useIsMobile();

  const [serviceStart, setServiceStart] = useState('roofing');
  const serviceSettings = useQuery({ queryKey: ['proposal-service-settings'], queryFn: async () => (await apiClient.get('/api/proposal-service-templates/settings/')).data as { enabled: boolean } });
  const serviceTemplates = useQuery({ queryKey: ['proposal-service-templates'], enabled: isNew && !!serviceSettings.data?.enabled, queryFn: async () => { const data = (await apiClient.get('/api/proposal-service-templates/')).data as ServiceTemplate[] | { results: ServiceTemplate[] }; return Array.isArray(data) ? data : data.results; } });
  const startingConfig = (): ServiceConfiguration | undefined => {
    if (serviceStart.startsWith('template:')) { const template = serviceTemplates.data?.find(t => t.id === Number(serviceStart.split(':')[1])); return template?.configuration ? { ...template.configuration, services: template.configuration.services.map(service => ({ ...duplicateService(service), template_version: template.version_id })) } : undefined; }
    if (serviceStart === 'roofing') return undefined;
    const item = starterTypes[Number(serviceStart)];
    return item ? { version: 2, include_roofing: false, services: [startingService(item.trade, item.variant)] } : undefined;
  };
  // New proposal form state
  const [selectedOpportunity, setSelectedOpportunity] = useState<any>(null);
  const opportunitySelectionChanged = useRef(false);
  useEffect(() => {
    if (!isNew || !opportunityId) return;
    let cancelled = false;
    opportunitySelectionChanged.current = false;
    setSelectedOpportunity(null);
    // Linked records may be closed/converted or outside the first list page.
    // Fetch the exact record instead of relying on the open-opportunity picker.
    apiClient.get(`/api/crm/opportunities/${encodeURIComponent(opportunityId)}/`)
      .then(response => {
        if (!cancelled && !opportunitySelectionChanged.current) setSelectedOpportunity(response.data);
      })
      .catch(() => {
        if (!cancelled && !opportunitySelectionChanged.current) toast.error('Could not load the linked opportunity. Select an opportunity to continue.');
      });
    return () => { cancelled = true; };
  }, [isNew, opportunityId]);

  const [totalSquares, setTotalSquares] = useState<string>('');
  const [selectedManufacturer, setSelectedManufacturer] = useState<number | null>(null);
  const [measurementSource, setMeasurementSource] = useState<'uploaded' | 'quick' | 'manual' | null>(null);
  const [squaresBreakdown, setSquaresBreakdown] = useState<string | null>(null);
  const [measurementPdfUrl, setMeasurementPdfUrl] = useState<string | null>(null);

  // Fetch proposal if editing
  const { data: proposal, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: ['proposal', id],
    queryFn: () => proposalsApi.get(parseInt(id!)),
    enabled: !isNew && !!id,
  });

  const { formData, hasChanges, isDirty, handleFieldChange, handleSave, updateMutation, validationMessage, saveError, recovery, recover, discardRecovery } = useProposalDraft(proposal, id);
  const serviceConfiguration = formData.service_configuration ?? proposal?.service_configuration;
  const includesRoofing = serviceConfiguration?.include_roofing !== false;
  const visibleTab = !includesRoofing && (activeTab === 1 || activeTab === 2)
    ? 5 : activeTab ?? (includesRoofing ? 0 : 5);
  // Creation navigates from onSuccess while its mutation is still pending.
  // Only existing proposals have editor drafts / saves to protect here.
  const blocker = useBlocker(() => !isNew && (isDirty() || pendingDrafts() > 0 || queryClient.isMutating({ predicate: mutation => !mutation.options.meta?.skipEditorFlush }) > 0));
  useEffect(() => {
    if (blocker.state === 'blocked') {
      if (window.confirm('Leave the builder? Unsaved settings remain available as a recoverable draft.')) blocker.proceed();
      else blocker.reset();
    }
  }, [blocker]);
  useEffect(() => {
    if (!hasChanges && !childDraftCount && !sharing) return;
    const prevent = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', prevent);
    return () => window.removeEventListener('beforeunload', prevent);
  }, [hasChanges, childDraftCount, sharing]);
  const saveBeforeSharing = async () => {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    flushDrafts();
    await handleSave();
    // Wait for already-running child edits as well as debounced text awaiting an echo.
    const deadline = Date.now() + 15000;
    while (pendingDrafts() || queryClient.isMutating({ predicate: mutation => !mutation.options.meta?.skipEditorFlush })) {
      if (Date.now() > deadline) throw new Error('Some edits have not saved. Retry or correct them before sharing.');
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    await queryClient.invalidateQueries({ queryKey: ['proposal', id] });
  };
  const runReviewedAction = async (action: () => Promise<unknown> | void) => {
    setSharing(true);
    const requestedId = id;
    try { await saveBeforeSharing(); if (routeId.current !== requestedId) throw new Error('The open proposal changed. Try the action again.'); await action(); }
    catch (error) { toast.error(error instanceof Error ? error.message : 'Save failed. Nothing was shared.'); }
    finally { setSharing(false); }
  };

  // Fetch opportunities for new proposal (only open/active, not won or lost)
  const { data: opportunities = [] } = useQuery({
    queryKey: ['opportunities', 'open'],
    queryFn: async () => {
      const response = await apiClient.get<any>('/api/crm/opportunities/?is_closed=false&page_size=1000');
      return response.data?.results || response.data || [];
    },
    enabled: isNew,
  });

  // Fetch proposal template for default manufacturer
  const { data: proposalTemplate } = useQuery({
    queryKey: ['proposal-template', selectedProposalType],
    queryFn: () => proposalsApi.getTemplateByType(selectedProposalType),
    enabled: isNew,
  });

  // Fetch manufacturers for dropdown
  const { data: manufacturers = [] } = useQuery({
    queryKey: ['manufacturers'],
    queryFn: async () => {
      const response = await apiClient.get<any>('/api/manufacturers/');
      return response.data?.results || response.data || [];
    },
    enabled: isNew,
  });

  // Full product catalog (grouped by manufacturer) for the per-tier product picker
  const { data: productCatalog = [] } = useQuery({
    queryKey: ['product-pricing-by-manufacturer'],
    queryFn: () => proposalsApi.getProductPricingByManufacturer(),
    enabled: !isNew,
  });

  // Fetch measurements when opportunity is selected
  // Priority: 1. Uploaded measurement report (DetailedMeasurement), 2. Quick Measurement
  useEffect(() => {
    let cancelled = false;
    manualMeasurement.current = false;
    setTotalSquares('');
    setSquaresBreakdown(null);
    setMeasurementPdfUrl(null);
    setMeasurementSource('manual');
    setMeasurementContext({ opportunity_id: selectedOpportunity?.id, source: 'manual' });
    if (selectedOpportunity?.id) {
      // First, try to get uploaded measurement report (DetailedMeasurement)
      apiClient.get<{ results: DetailedMeasurement[] }>(`/api/measurements/detailed-measurements-by-opportunity/?opportunity_id=${selectedOpportunity.id}`)
        .then(res => {
          if (cancelled || manualMeasurement.current) return;
          const detailedMeasurements = Array.isArray(res.data) ? res.data : res.data?.results || [];
          const isExternalReport = (m: DetailedMeasurement) =>
            m.is_external_upload ?? (!m.measurement && (Boolean(m.source_document) || m.source_type !== 'other'));
          // Prefer external uploaded report first, then fall back to synced DetailedMeasurement from Quick Measurement
          const completedMeasurement =
            detailedMeasurements.find(
              (m: DetailedMeasurement) => isExternalReport(m) && m.parsing_status === 'completed' && m.total_squares
            ) ||
            detailedMeasurements.find(
              (m: DetailedMeasurement) => m.parsing_status === 'completed' && m.total_squares
            );

          if (completedMeasurement && completedMeasurement.total_squares) {
            const sourceType: 'uploaded' | 'quick' = isExternalReport(completedMeasurement) ? 'uploaded' : 'quick';
            // Apply waste factor if available (waste_factor is a percentage like 10, 15, 20)
            const wasteFactor = completedMeasurement.waste_factor || 0;
            const baseSquares = completedMeasurement.total_squares * (1 + wasteFactor / 100);
            let adjustedSquares = baseSquares;
            let breakdown = `${baseSquares.toFixed(2)} sq`;

            // Add Hip & Ridge squares: (hips + ridges) / 33 = bundles, bundles / 3 = squares
            const hipsRidgesFt = parseFloat(String(completedMeasurement.total_hips_ft || 0)) + parseFloat(String(completedMeasurement.total_ridges_ft || 0));
            if (hipsRidgesFt > 0) {
              const hipRidgeBundles = Math.ceil(hipsRidgesFt / 33);
              const hipRidgeSquares = hipRidgeBundles / 3;
              adjustedSquares += hipRidgeSquares;
              breakdown += ` + ${hipRidgeSquares.toFixed(2)} hip/ridge`;
            }

            // Add Starter squares: (eaves + rakes) / 100 = bundles, bundles / 3 = squares
            const eavesRakesFt = parseFloat(String(completedMeasurement.total_eaves_ft || 0)) + parseFloat(String(completedMeasurement.total_rakes_ft || 0));
            if (eavesRakesFt > 0) {
              const starterBundles = Math.ceil(eavesRakesFt / 100);
              const starterSquares = starterBundles / 3;
              adjustedSquares += starterSquares;
              breakdown += ` + ${starterSquares.toFixed(2)} starter`;
            }

            const finalSquares = roundUpToThird(adjustedSquares);
            breakdown += ` = ${finalSquares} sq`;

            setMeasurementContext({ opportunity_id: selectedOpportunity.id, source: sourceType, base_squares: completedMeasurement.total_squares, waste_percent: wasteFactor, breakdown, measured_squares: finalSquares });
            setTotalSquares(finalSquares.toString());
            setSquaresBreakdown(breakdown);
            setMeasurementPdfUrl(completedMeasurement.source_document_url || null);
            setMeasurementSource(sourceType);
            return; // Don't fall through to Quick Measurement
          }

          // Fall back to Quick Measurement if no uploaded report
          return apiClient.get<any>(`/api/measurements/?opportunity=${selectedOpportunity.id}`);
        })
        .then(res => {
          // Only process if we didn't already set from DetailedMeasurement (res is undefined when we returned early)
          if (res && !cancelled && !manualMeasurement.current) {
            const measurements = res.data?.results || res.data || [];
            if (measurements.length > 0) {
              const measurement = measurements[0];
              // Calculate total squares from measurement data
              // Using the same formula as QuickEstimatePanel
              const pitchFactors: Record<string, number> = {
                low: 1.013794,
                average: 1.118034,
                steep: 1.3,
              };
              const complexityFactors: Record<string, number> = {
                low: 0.10,
                moderate: 0.15,
                high: 0.20,
              };
              const pitch = measurement.estimated_pitch || 'average';
              const complexity = measurement.estimated_complexity || 'moderate';
              const totalArea = measurement.total_roof_area || 0;

              if (totalArea > 0) {
                const pitchFactor = pitchFactors[pitch] || 1.118034;
                const complexityFactor = complexityFactors[complexity] || 0.15;
                const adjustedArea = totalArea * pitchFactor * (1 + complexityFactor);
                const squares = roundUpToThird(adjustedArea / 100);
                setTotalSquares(squares.toString());
                setMeasurementSource('quick');
                setMeasurementContext({ opportunity_id: selectedOpportunity.id, source: 'quick', base_squares: totalArea / 100, waste_percent: complexityFactor * 100, pitch_factor: pitchFactor, measured_squares: squares });
              }
            }
          }
        })
        .catch(() => {
          // No measurement found, leave total squares empty for manual entry
          if (!cancelled && !manualMeasurement.current) setMeasurementSource('manual');
        });
    }
    return () => { cancelled = true; };
  }, [selectedOpportunity?.id]);

  // Pre-populate manufacturer from template when it loads
  useEffect(() => {
    if (proposalTemplate?.default_featured_manufacturer && !selectedManufacturer) {
      setSelectedManufacturer(proposalTemplate.default_featured_manufacturer);
    }
  }, [proposalTemplate]);

  // Initialize form data when proposal loads
  useEffect(() => {
    if (proposal) {
      // Select first page by default
      if (proposal.pages.length > 0 && !selectedPageId) {
        setSelectedPageId(proposal.pages[0].id);
      }
    }
  }, [proposal]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: {
      service_configuration?: ServiceConfiguration;
      title?: string;
      opportunity: number;
      proposal_type?: ProposalType;
      total_squares?: number | null;
      featured_manufacturer?: number | null;
      measurement_context?: Record<string, unknown>;
    }) => proposalsApi.create(data),
    onSuccess: (newProposal) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] });
      toast.success('Proposal created');
      navigate(`/crm/proposals/${newProposal.id}/edit${serviceStart !== 'roofing' ? '?tab=services' : ''}`, { replace: true });
    },
    onError: () => {
      toast.error('Failed to create proposal');
    },
  });

  // Page toggle mutation
  const togglePageMutation = useMutation({
    mutationFn: ({ pageId, enabled }: { pageId: number; enabled: boolean }) =>
      proposalsApi.updatePage(pageId, { is_enabled: enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proposal', id] });
    },
  });

  // Add custom page mutation
  const addCustomPageMutation = useMutation({
    mutationFn: () => {
      const existingPages = proposal?.pages ?? [];
      const nextSortOrder = existingPages.length
        ? Math.max(...existingPages.map((p) => p.sort_order)) + 1
        : 0;
      return proposalsApi.createPage({
        proposal: parseInt(id!),
        page_type: 'custom',
        title: 'Custom Page',
        is_enabled: true,
        sort_order: nextSortOrder,
      });
    },
    onSuccess: (newPage) => {
      queryClient.invalidateQueries({ queryKey: ['proposal', id] });
      setSelectedPageId(newPage.id);
      toast.success('Custom page added');
    },
    onError: () => {
      toast.error('Failed to add custom page');
    },
  });

  // Reorder pages mutation (persists new sort order via bulk-update)
  const reorderPagesMutation = useMutation({
    mutationFn: (reordered: ProposalPage[]) =>
      proposalsApi.bulkUpdatePages(
        reordered.map((page, index) => ({ id: page.id, sort_order: index }))
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proposal', id] });
      toast.success('Page order updated');
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['proposal', id] });
      toast.error('Failed to update page order. The saved order has been reloaded.');
    },
  });

  // Tier update mutation
  const updateTierMutation = useMutation({
    mutationFn: ({ tierId, data }: { tierId: number; data: any }) =>
      proposalsApi.updateTier(tierId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proposal', id] });
    },
  });

  // Tier product override mutation
  const setTierProductMutation = useMutation({
    mutationFn: ({ tierId, productId }: { tierId: number; productId: number | null }) =>
      proposalsApi.setTierProduct(tierId, productId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proposal', id] });
    },
    onError: () => {
      toast.error('Failed to update tier product');
    },
  });

  // Line item mutations
  const addLineItemMutation = useMutation({
    mutationFn: (tierId: number) =>
      proposalsApi.createLineItem({
        tier: tierId,
        description: 'New item',
        quantity: 1,
        unit: 'ea',
        unit_price: 0,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proposal', id] });
    },
  });

  const updateLineItemMutation = useMutation({
    mutationFn: ({ itemId, data }: { itemId: number; data: any }) =>
      proposalsApi.updateLineItem(itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proposal', id] });
    },
  });

  const reorderLineItemsMutation = useMutation({
    mutationFn: (reordered: ProposalLineItem[]) =>
      apiClient.post('/api/proposal-line-items/reorder/', { tier: proposal?.tiers.find(tier => tier.line_items.some(item => item.id === reordered[0]?.id))?.id, ids: reordered.map(item => item.id) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proposal', id] });
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['proposal', id] });
      toast.error('Line items changed or could not be reordered. The saved order has been reloaded.');
    },
  });

  const deleteLineItemMutation = useMutation({
    mutationFn: (itemId: number) => proposalsApi.deleteLineItem(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proposal', id] });
    },
  });

  const pdf = useProposalPdf(Number(id), proposal?.proposal_number || 'Proposal');

  const handleCreate = () => {
    if (!selectedOpportunity) return;
    if (dealType !== 'retail') {
      createDeal.mutate({opportunity:selectedOpportunity.id,deal_type:dealType,proposal_type:selectedProposalType});
      return;
    }
    const serviceConfig = startingConfig();
    createMutation.mutate({
      ...(serviceConfig ? { service_configuration: serviceConfig, title: (serviceConfig.services.map(s => s.name).join(' + ') + ' proposal').slice(0, 200) } : {}),
      opportunity: selectedOpportunity.id,
      proposal_type: selectedProposalType,
      total_squares: totalSquares ? parseFloat(totalSquares) : null,
      featured_manufacturer: serviceConfig && !serviceConfig.include_roofing ? null : selectedManufacturer,
      measurement_context: measurementContext,
    });
  };

  if (proposal && (proposal.acceptance_date || proposal.status === 'accepted' || (proposal.service_configuration?.version && (proposal.status !== 'draft' || proposal.sent_at)))) {
    return <Alert color="success" sx={{ m: 3 }} endDecorator={<Button onClick={() => navigate(`/crm/proposals/${id}`)}>View acceptance and create revision</Button>}>This proposal has been shared or accepted. Create a revision to change its scope or prices.</Alert>;
  }

  // New proposal form
  if (isNew) {
    return (
      <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 700, mx: 'auto' }}>
        <Button
          variant="plain"
          startDecorator={<ArrowBack />}
          onClick={() => navigate('/crm/proposals')}
          sx={{ mb: 2 }}
        >
          Back to Proposals
        </Button>

        <Card>
          <CardContent>
            <Typography level="h4" sx={{ mb: 3 }}>
              Create Proposal
            </Typography>

            <FormControl sx={{ mb: 3 }}>
              <FormLabel>Choose the deal workflow</FormLabel>
              <Box className="deal-type-options" sx={{ mt: 1 }}>
                {([
                  { type: 'retail', label: 'Retail', icon: <Home size={23} />, description: 'Packages, product selections, and a construction contract.' },
                  { type: 'insurance', label: 'Insurance', icon: <ShieldCheck size={23} />, description: 'Inspection, claim assistance, and a contingency agreement.' },
                  { type: 'repair', label: 'Repair', icon: <Build size={23} />, description: 'A specific scope, clear pricing, and repair authorization.' },
                ] as const).map(({ type, label, icon, description }) => (
                  <button className="deal-type-choice" key={type} aria-pressed={dealType === type}
                    onClick={() => { setDealType(type); dealRequest.current = crypto.randomUUID(); }}
                    disabled={type !== 'retail' && !dealSettings.data?.[`${type}_enabled`]}>
                    {icon}<strong>{label}{dealType === type && <CheckCircle size={18} weight="fill" />}</strong><small>{description}</small>
                  </button>
                ))}
              </Box>
              {(!dealSettings.data?.insurance_enabled || !dealSettings.data?.repair_enabled) && <Typography level="body-xs" sx={{ mt: 1.5 }}>Additional workflows can be enabled in Proposal Settings → Insurance & Repair.</Typography>}
            </FormControl>
            {dealType === 'retail' && serviceSettings.data?.enabled && <FormControl sx={{ mb: 3 }}><FormLabel>Start with</FormLabel><Select value={serviceStart} onChange={(_, value) => setServiceStart(value || 'roofing')}><Option value="roofing">Roofing</Option>{serviceTemplates.data?.filter(t => !t.is_archived && t.configuration).map(t => <Option key={t.id} value={`template:${t.id}`}>{t.is_favorite ? '★ ' : ''}{t.name}</Option>)}{starterTypes.map((item, i) => <Option value={String(i)} key={i}>{tradeNames[item.trade]} · {item.variant}</Option>)}</Select><Typography level="body-xs">Start with a service or saved combination. Add more work in the same proposal.</Typography></FormControl>}
            <FormControl sx={{ mb: 3 }}>
              <FormLabel>Project Type</FormLabel>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
                {PROPOSAL_TYPES.map((type) => (
                  <Card
                    key={type.value}
                    variant={selectedProposalType === type.value ? 'solid' : 'outlined'}
                    color={selectedProposalType === type.value ? 'primary' : 'neutral'}
                    sx={{
                      flex: 1,
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.2s',
                      '&:hover': { borderColor: 'primary.500' },
                    }}
                    onClick={() => setSelectedProposalType(type.value)}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ mb: 1 }}>{type.icon}</Box>
                      <Typography
                        level="title-sm"
                        sx={{
                          color: selectedProposalType === type.value ? 'inherit' : 'text.primary',
                        }}
                      >
                        {type.label}
                      </Typography>
                      <Typography
                        level="body-xs"
                        sx={{
                          color: selectedProposalType === type.value ? 'inherit' : 'text.tertiary',
                        }}
                      >
                        {type.description}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </FormControl>

            <Divider sx={{ my: 3 }} />

            <FormControl sx={{ mb: 3 }}>
              <FormLabel>Select Opportunity</FormLabel>
              <Autocomplete
                placeholder="Search opportunities..."
                options={selectedOpportunity && !opportunities.some((opp: any) => opp.id === selectedOpportunity.id)
                  ? [selectedOpportunity, ...opportunities] : opportunities}
                value={selectedOpportunity}
                getOptionLabel={(opp: any) =>
                  `${opp.name}${opp.contact_name ? ` - ${opp.contact_name}` : ''}`
                }
                isOptionEqualToValue={(option: any, value: any) => option.id === value?.id}
                onChange={(_, value: any) => {
                  opportunitySelectionChanged.current = true;
                  setSelectedOpportunity(value);
                  if (!value) {
                    setTotalSquares('');
                    setMeasurementSource(null);
                    setSquaresBreakdown(null);
                    setMeasurementPdfUrl(null);
                  }
                }}
                loading={!opportunities.length}
              />
            </FormControl>

            {/* Show additional fields after opportunity is selected */}
            {selectedOpportunity && (
              <>
                {dealType === 'retail' && <>
                {(serviceStart === 'roofing' || startingConfig()?.include_roofing) && <>
                <FormControl sx={{ mb: 3 }}>
                  <FormLabel>Total Squares (including waste)</FormLabel>
                  <Input
                    type="number"
                    placeholder="Enter total squares..."
                    value={totalSquares}
                    onChange={(e) => { manualMeasurement.current = true; setTotalSquares(e.target.value); setMeasurementSource('manual'); setMeasurementContext(previous => ({ ...previous, source: 'manual', manual_squares: e.target.value })); }}
                    endDecorator={<Typography level="body-sm">squares</Typography>}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      {(measurementSource === 'uploaded' || measurementSource === 'quick') && squaresBreakdown
                        ? squaresBreakdown
                        : measurementSource === 'uploaded'
                        ? 'Pre-populated from uploaded measurement report. Adjust if needed.'
                        : measurementSource === 'quick'
                        ? 'Pre-populated from Quick Measurement. Adjust if needed.'
                        : 'No measurement found. Enter manually.'}
                    </Typography>
                    {measurementPdfUrl && (
                      <Typography
                        level="body-xs"
                        component="a"
                        href={measurementPdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: 'var(--brand-text, #7013f5)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        View PDF
                      </Typography>
                    )}
                  </Box>
                </FormControl>

                <FormControl sx={{ mb: 3 }}>
                  <FormLabel>Featured Manufacturer</FormLabel>
                  <Autocomplete
                    placeholder="Select manufacturer..."
                    options={manufacturers}
                    value={manufacturers.find((m: any) => m.id === selectedManufacturer) || null}
                    getOptionLabel={(mfr: any) => mfr.name}
                    isOptionEqualToValue={(option: any, value: any) => option?.id === value?.id}
                    onChange={(_, value: any) => {
                      setSelectedManufacturer(value?.id || null);
                    }}
                  />
                  <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                    {proposalTemplate?.default_featured_manufacturer ? 'Pre-populated from Proposal Settings.' : 'Optional. Select to feature their marketing materials.'}
                  </Typography>
                </FormControl>

                </>}
                <Button
                  variant="solid"
                  color="primary"
                  size="lg"
                  fullWidth
                  onClick={handleCreate}
                  loading={createMutation.isPending}
                  disabled={(serviceStart === 'roofing' || !!startingConfig()?.include_roofing) && !totalSquares}
                >
                  Create Proposal
                </Button>
                </>}
                {dealType !== 'retail' && <Button loading={createDeal.isPending} disabled={!dealSettings.data?.[`${dealType}_enabled`]} onClick={handleCreate} size="lg" fullWidth>Start {dealType === 'insurance' ? 'Insurance' : 'Repair'} Proposal</Button>}
              </>
            )}

            <Divider sx={{ my: 3 }} />

            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              {dealType === 'retail' ? 'The proposal will be pre-populated with default line items based on the selected proposal type.' : dealType === 'insurance' ? 'Start an inspection checklist, track claim assistance, and prepare a contingency agreement.' : 'Define and price the specific repair scope, or import Maintenance findings.'}{' '}
              You can customize these defaults in{' '}
              <Typography
                component="a"
                href="/crm/proposals/settings"
                sx={{ color: 'var(--brand-text, #7013f5)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                Proposal Settings
              </Typography>
              .
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !proposal) {
    return (
      <Alert color="danger" sx={{ m: 3 }}>
        Error loading proposal. Please try again.
      </Alert>
    );
  }

  const selectedPage = proposal.pages.find((p) => p.id === selectedPageId);

  const pageListEl = (
    <PageList
      pages={proposal.pages}
      selectedPageId={selectedPageId}
      onSelectPage={(pageId) => {
        setSelectedPageId(pageId);
        setActiveTab(0);
        setPagesDrawerOpen(false);
      }}
      onTogglePage={(pageId, enabled) => {
        togglePageMutation.mutate({ pageId, enabled });
      }}
      onReorderPages={(reordered) => { if (!reorderPagesMutation.isPending) reorderPagesMutation.mutate(reordered); }}
      onAddCustomPage={() => addCustomPageMutation.mutate()}
    />
  );

  return (
    <Box sx={{ display: 'flex', height: { xs: 'auto', md: 'calc(100vh - 64px)' } }}>
      {/* Left Panel - Page List (desktop) */}
      {!isMobile && (
        <Box
          sx={{
            width: 280,
            borderRight: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.surface',
            flexShrink: 0,
          }}
        >
          {pageListEl}
        </Box>
      )}

      {/* Page List Drawer (mobile) */}
      <Drawer
        open={pagesDrawerOpen}
        onClose={() => setPagesDrawerOpen(false)}
        size="sm"
        slotProps={{ content: { sx: { width: 300 } } }}
      >
        {pageListEl}
      </Drawer>

      {/* Right Panel - Editor */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: { xs: 'visible', md: 'hidden' },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: { xs: 1.5, md: 2 },
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Button
            variant="plain"
            size="sm"
            startDecorator={<ArrowBack />}
            onClick={() => navigate('/crm/proposals')}
          >
            Back
          </Button>

          {isMobile && (
            <Button
              variant="outlined"
              color="primary"
              size="sm"
              startDecorator={<PagesIcon />}
              onClick={() => setPagesDrawerOpen(true)}
            >
              Pages
            </Button>
          )}

          <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 0 } }}>
            <Typography level="title-lg" sx={{ wordBreak: 'break-word' }}>
              {proposal.proposal_number} - {formData.title || proposal.title}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip size="sm" variant="soft">
                {proposal.status}
              </Chip>
              {proposal.opportunity_name && (
                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                  <Home style={{ marginRight: 4 }} />
                  {proposal.opportunity_name}
                </Typography>
              )}
              {proposal.contact_name && (
                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                  <Person style={{ marginRight: 4 }} />
                  {proposal.contact_name}
                </Typography>
              )}
            </Stack>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              color="primary"
              size="sm"
              startDecorator={<Visibility />}
              onClick={() => runReviewedAction(() => setPreviewOpen(true))}
              loading={sharing}
            >
              Preview
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="sm"
              startDecorator={<PictureAsPdf />}
              onClick={() => runReviewedAction(() => pdf.generate())}
              loading={pdf.isPending || sharing}
            >
              PDF
            </Button>
            {['draft', 'sent', 'viewed', 'expired'].includes(proposal.status) && (
              <Button
                variant="solid"
                size="sm"
                color="success"
                startDecorator={<Send />}
                onClick={() => runReviewedAction(() => navigate(`/crm/proposals/${id}?review=1`))}
                disabled={sharing}
              >
                {proposal.status === 'draft' ? 'Send' : 'Resend'}
              </Button>
            )}
            <Button
              variant="solid"
              size="sm"
              startDecorator={<Save />}
              onClick={() => runReviewedAction(() => {})}
              loading={updateMutation.isPending}
              disabled={!hasChanges && !childDraftCount}
            >
              Save
            </Button>
          </Stack>
        </Box>

        <Box sx={{ px: 2, pt: 1 }}>
          <Typography level="body-sm" role="status" color={validationMessage ? 'warning' : updateMutation.isError ? 'danger' : 'neutral'}>
            {validationMessage ? `Autosave paused · ${validationMessage} Draft retained in this browser.` : updateMutation.isError ? `Save failed · ${saveError} Draft retained.` : activeEdits || childDraftCount ? 'Saving changes…' : hasChanges ? 'Unsaved changes' : 'Saved'}
          </Typography>
          {pdf.message && <Typography role="status" color={pdf.isError ? "danger" : "neutral"}>{pdf.message}</Typography>}
          {recovery && <Alert color="warning" endDecorator={<Stack direction="row" spacing={1}><Button onClick={recover}>Recover draft</Button><Button variant="plain" onClick={discardRecovery}>Discard</Button></Stack>}>Unsaved settings from an earlier visit are available.</Alert>}
          <ProposalBuilderTools showTools={false} includesRoofing={includesRoofing} proposal={proposal} savedAt={dataUpdatedAt} previewOpen={previewOpen} onClosePreview={() => setPreviewOpen(false)} beforeAction={saveBeforeSharing} onFieldChange={handleFieldChange} />
        </Box>
        {/* Main Content */}
        <Box sx={{ flex: 1, overflow: { xs: 'visible', md: 'auto' }, p: { xs: 1.5, md: 3 } }}>
          <Tabs value={visibleTab} onChange={(_, value) => setActiveTab(value as number)}>
            <TabList sx={{ flexWrap: 'wrap', '& > *': { flex: { xs: '1 1 45%', sm: '0 0 auto' } } }}>
              <Tab value={0}>Page Settings</Tab>
              {includesRoofing && <Tab value={1}>Pricing Tiers</Tab>}
              {includesRoofing && <Tab value={2}>Add-ons & Fees</Tab>}
              <Tab value={3}>Proposal Settings</Tab>
              <Tab value={4}>Presentation</Tab>
              <Tab value={5}>Services</Tab>
            </TabList>

            {/* Page Settings Tab */}
            <TabPanel value={0}>
              {selectedPage ? (
                <PageEditor
                  page={selectedPage}
                  includesRoofing={includesRoofing}
                  proposal={proposal}
                  onUpdate={(data) => {
                    proposalsApi.updatePage(selectedPage.id, data).then(() => {
                      queryClient.invalidateQueries({ queryKey: ['proposal', id] });
                    });
                  }}
                />
              ) : (
                <Alert color="neutral">
                  Select a page from the left panel to edit its settings.
                </Alert>
              )}
            </TabPanel>

            {/* Pricing Tiers Tab */}
            {includesRoofing && <TabPanel value={1}>
              <TierEditor
                tiers={proposal.tiers}
                totalSquares={proposal.total_squares}
                taxEnabled={formData.tax_enabled ?? proposal.tax_enabled}
                taxRate={formData.tax_rate ?? proposal.tax_rate}
                productCatalog={productCatalog}
                featuredManufacturerId={
                  formData.featured_manufacturer ?? proposal.featured_manufacturer
                }
                onUpdateTier={(tierId, data) => updateTierMutation.mutate({ tierId, data })}
                onSetTierProduct={(tierId, productId) =>
                  setTierProductMutation.mutate({ tierId, productId })
                }
                onAddLineItem={(tierId) => addLineItemMutation.mutate(tierId)}
                onUpdateLineItem={(itemId, data) => updateLineItemMutation.mutate({ itemId, data })}
                onDeleteLineItem={(itemId) => deleteLineItemMutation.mutate(itemId)}
                onReorderLineItems={(reordered) => { if (!reorderLineItemsMutation.isPending) reorderLineItemsMutation.mutate(reordered); }}
              />
            </TabPanel>}

            {/* Add-ons & Fees Tab */}
            {includesRoofing && <TabPanel value={2}>
              <OptionalUpgradesEditor
                upgrades={proposal.optional_upgrades}
                proposalId={proposal.id}
                onRefresh={() => queryClient.invalidateQueries({ queryKey: ['proposal', id] })}
              />
            </TabPanel>}

            <TabPanel value={4} keepMounted>
              <Stack spacing={3}>
                <PresentationSettingsEditor
                  value={formData.presentation_options || proposal.presentation_options || {}}
                  onChange={value => handleFieldChange('presentation_options', value)}
                  brand={proposal.presentation?.brand}
                  inspectionPageEnabled={proposal.pages.some(page => page.page_type === 'inspection' && page.is_enabled)}
                />
                <ProposalPhotoSelection key={proposal.id} id={proposal.id} />
              </Stack>
            </TabPanel>
            <TabPanel value={5} keepMounted>
              {!serviceSettings.data?.enabled ? <Alert>An Org Admin can enable project services in Proposal Settings → Services & Templates.</Alert> : <ServiceEditor proposalId={proposal.id} value={formData.service_configuration || proposal.service_configuration} onChange={value => handleFieldChange('service_configuration', value)} />}
            </TabPanel>
            {/* Proposal Settings Tab */}
            <TabPanel value={3}>
              <ProposalSettingsEditor
                proposal={proposal}
                formData={formData}
                onFieldChange={handleFieldChange}
              />
            </TabPanel>
          </Tabs>
        </Box>
      </Box>

      {/* Send review is performed on the proposal detail screen after saving. */}

    </Box>
  );
}

// Page Editor Component
const PageEditor: React.FC<{
  page: ProposalPage;
  includesRoofing: boolean;
  proposal: ProposalDetail;
  onUpdate: (data: Partial<ProposalPage>) => void;
}> = ({ page, proposal, includesRoofing, onUpdate }) => {
  return (
    <Box>
      <Typography level="title-lg" sx={{ mb: 2 }}>
        {page.title || page.page_type.replace('_', ' ')} Settings
      </Typography>

      <Stack spacing={3}>
        <FormControl>
          <FormLabel>Page Title</FormLabel>
          <DebouncedInput
            value={page.title || ''}
            onCommit={(value) => onUpdate({ title: value })}
            placeholder="Page title..."
          />
        </FormControl>

        <FormControl>
          <FormLabel>Intro Text (optional)</FormLabel>
          <DebouncedTextarea
            minRows={2}
            value={page.intro_text || ''}
            onCommit={(value) => onUpdate({ intro_text: value })}
            placeholder="Optional introduction text displayed below the page heading..."
          />
          <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
            This text appears below the page heading on the rendered proposal.
          </Typography>
        </FormControl>

        <FormControl>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Switch
              checked={page.is_enabled}
              onChange={(e) => onUpdate({ is_enabled: e.target.checked })}
            />
            <Typography level="body-md">Include this page in proposal</Typography>
          </Stack>
        </FormControl>

        {/* Page-specific settings based on type */}
        {page.page_type === 'title' && (
          <Alert color="neutral">
            Title page settings are configured in the Proposal Settings tab.
          </Alert>
        )}

        {page.page_type === 'introduction' && (
          <Alert color="neutral">
            Introduction content is configured in the Proposal Settings tab.
          </Alert>
        )}

        {page.page_type === 'inspection' && (
          <Alert color="neutral">
            Inspection photos are pulled from the linked Opportunity.
          </Alert>
        )}

        {page.page_type === 'measurements' && (
          <Alert color="neutral">
            Measurements are pulled from the linked Opportunity.
          </Alert>
        )}

        {page.page_type === 'quote_details' && (
          <Alert color="neutral">
            {includesRoofing ? 'Roofing quote details are configured in Pricing Tiers. Additional project work is configured in Services.' : 'Quantities, prices, options, fees and discounts are configured in the Services tab.'}
          </Alert>
        )}

        {page.page_type === 'authorization' && (
          <Alert color="neutral">
            {includesRoofing ? 'Authorization page includes package selections, add-ons, and signature fields.' : 'Authorization includes service selections, the project total, and signature fields.'}
          </Alert>
        )}

        {page.page_type === 'terms' && (
          <Alert color="neutral">
            Terms and conditions are configured in the Proposal Settings tab.
          </Alert>
        )}

        {page.page_type === 'manufacturer_resource' && (
          <FormControl>
            <FormLabel>Manufacturer Resource</FormLabel>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              {page.manufacturer_resource_title || 'No resource selected'}
            </Typography>
          </FormControl>
        )}

        {page.page_type === 'manufacturer_marketing' && (
          <Alert color="neutral">
            This page displays the Featured Manufacturer's General Marketing Material PDF.
            {!proposal.featured_manufacturer && (
              <Typography level="body-xs" sx={{ mt: 1, color: 'warning.600' }}>
                No manufacturer selected. Set a Featured Manufacturer in the Proposal Settings tab.
              </Typography>
            )}
          </Alert>
        )}

        {page.page_type === 'manufacturer_colors' && (
          <Alert color="neutral">
            This page displays the Featured Manufacturer's Colors & Options PDF.
            {!proposal.featured_manufacturer && (
              <Typography level="body-xs" sx={{ mt: 1, color: 'warning.600' }}>
                No manufacturer selected. Set a Featured Manufacturer in the Proposal Settings tab.
              </Typography>
            )}
          </Alert>
        )}

        {page.page_type === 'manufacturer_other' && (
          <Alert color="neutral">
            This page displays the Featured Manufacturer's Other Marketing Assets PDF.
            {!proposal.featured_manufacturer && (
              <Typography level="body-xs" sx={{ mt: 1, color: 'warning.600' }}>
                No manufacturer selected. Set a Featured Manufacturer in the Proposal Settings tab.
              </Typography>
            )}
          </Alert>
        )}
      </Stack>
    </Box>
  );
};

// Manufacturer type for dropdown
interface ManufacturerOption {
  id: number;
  name: string;
  logo: string | null;
}

// Proposal Settings Editor
const ProposalSettingsEditor: React.FC<{
  proposal: ProposalDetail;
  formData: Partial<ProposalDetail>;
  onFieldChange: (field: string, value: any) => void;
}> = ({ proposal, formData, onFieldChange }) => {
  const queryClient = useQueryClient();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [uploadPreview, setUploadPreview] = React.useState<string | null>(null);
  const [showPhotoPicker, setShowPhotoPicker] = React.useState(false);
  // Tier names ride along on the proposal payload
  const tierLabels = proposal.tier_labels || DEFAULT_TIER_LABELS;
  const tierBlurb = `${tierLabels.good}/${tierLabels.better}/${tierLabels.best}`;

  // Fetch manufacturers for dropdown
  const { data: manufacturers = [] } = useQuery<ManufacturerOption[]>({
    queryKey: ['manufacturers'],
    queryFn: async () => {
      const response = await apiClient.get<any>('/api/manufacturers/');
      return response.data?.results || response.data || [];
    },
  });

  // Fetch opportunity photos
  const { data: opportunityPhotos = [], isLoading: isLoadingPhotos } = useQuery<any[]>({
    queryKey: ['opportunity-photos', proposal.opportunity],
    queryFn: async () => {
      const response = await apiClient.getOpportunityPhotos(proposal.opportunity);
      return response.data || [];
    },
    enabled: !!proposal.opportunity,
  });

  // Upload cover image mutation
  const uploadCoverImageMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('cover_image', file);
      return apiClient.patch(`/api/proposals/${proposal.id}/`, formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proposal', proposal.id.toString()] });
      setUploadPreview(null);
      toast.success('Cover image uploaded');
    },
    onError: () => {
      toast.error('Failed to upload cover image');
    },
  });

  // Remove cover image mutation
  const removeCoverImageMutation = useMutation({
    mutationFn: async () => {
      return apiClient.patch(`/api/proposals/${proposal.id}/`, { cover_image: null });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proposal', proposal.id.toString()] });
      toast.success('Custom image removed');
    },
    onError: () => {
      toast.error('Failed to remove cover image');
    },
  });

  // Set cover from opportunity photo mutation
  const setCoverFromPhotoMutation = useMutation({
    mutationFn: async (photoId: number) => {
      return apiClient.post(`/api/proposals/${proposal.id}/set-cover-from-photo/`, {
        photo_id: photoId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proposal', proposal.id.toString()] });
      setShowPhotoPicker(false);
      toast.success('Cover image set from photo');
    },
    onError: () => {
      toast.error('Failed to set cover image');
    },
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      // Upload the file
      uploadCoverImageMutation.mutate(file);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const selectedManufacturerId = formData.featured_manufacturer !== undefined
    ? formData.featured_manufacturer
    : proposal.featured_manufacturer;
  const currentImage = uploadPreview || proposal.cover_image || proposal.street_view_url;
  const isCustomImage = !!proposal.cover_image;

  return (
    <Box>
      <Typography level="title-lg" sx={{ mb: 3 }}>
        Proposal Settings
      </Typography>

      <Stack spacing={3} sx={{ maxWidth: 800 }}>
        {/* Basic Info */}
        <Card variant="outlined">
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Basic Information
            </Typography>
            <Stack spacing={2}>
              <ProposalNumberEditor key={proposal.id} proposal={proposal} />
              <FormControl>
                <FormLabel>Proposal Title</FormLabel>
                <Input
                  value={formData.title || proposal.title}
                  onChange={(e) => onFieldChange('title', e.target.value)}
                />
              </FormControl>

              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Report Type</FormLabel>
                  <Input
                    value={formData.report_type || proposal.report_type}
                    onChange={(e) => onFieldChange('report_type', e.target.value)}
                  />
                </FormControl>

                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Proposal Date</FormLabel>
                  <Input
                    type="date"
                    value={formData.proposal_date || proposal.proposal_date}
                    onChange={(e) => onFieldChange('proposal_date', e.target.value)}
                  />
                </FormControl>

                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Valid Until</FormLabel>
                  <Input
                    type="date"
                    value={formData.valid_until || proposal.valid_until || ''}
                    onChange={(e) => onFieldChange('valid_until', e.target.value)}
                  />
                </FormControl>
              </Box>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={3}
                alignItems={{ xs: 'stretch', sm: 'flex-end' }}
              >
                <FormControl sx={{ maxWidth: { xs: '100%', sm: 200 } }}>
                  <FormLabel>Total Squares</FormLabel>
                  <Input
                    type="number"
                    value={formData.total_squares ?? proposal.total_squares ?? ''}
                    onChange={(e) => onFieldChange('total_squares', parseFloat(e.target.value) || null)}
                    placeholder="e.g., 25"
                    endDecorator={<Typography level="body-sm">sq</Typography>}
                  />
                </FormControl>

                <FormControl orientation="horizontal" sx={{ gap: 1 }}>
                  <Switch
                    checked={formData.show_total_squares ?? proposal.show_total_squares ?? false}
                    onChange={(e) => onFieldChange('show_total_squares', e.target.checked)}
                  />
                  <Box>
                    <FormLabel sx={{ mb: 0 }}>Show on Pricing</FormLabel>
                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                      Display squares below prices
                    </Typography>
                  </Box>
                </FormControl>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Cover Image */}
        <Card variant="outlined">
          <CardContent>
            <Typography level="title-md" sx={{ mb: 1 }}>
              Cover Image
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
              The cover image appears on the proposal title page. By default, Google Street View is used.
            </Typography>

            {/* Current Image Preview */}
            {currentImage && (
              <Box sx={{ mb: 2, position: 'relative' }}>
                <Box
                  component="img"
                  src={currentImage}
                  alt="Cover"
                  sx={{
                    width: '100%',
                    maxWidth: 400,
                    height: 'auto',
                    maxHeight: 250,
                    objectFit: 'cover',
                    borderRadius: 'sm',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                />
                <Chip
                  size="sm"
                  variant="soft"
                  color={isCustomImage ? 'primary' : 'neutral'}
                  startDecorator={<ImageIcon size={14} />}
                  sx={{ position: 'absolute', top: 8, left: 8 }}
                >
                  {isCustomImage ? 'Custom Image' : 'Google Street View'}
                </Chip>
              </Box>
            )}

            {/* No image state */}
            {!currentImage && (
              <Box
                sx={{
                  mb: 2,
                  p: 4,
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 'sm',
                  textAlign: 'center',
                  maxWidth: 400,
                }}
              >
                <ImageIcon size={48} color="var(--joy-palette-text-tertiary)" style={{ marginBottom: 8 }} />
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  Choose a roof photo below for the first page of your proposal, or upload your own image.
                </Typography>
              </Box>
            )}

            {/* Upload Controls */}
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                startDecorator={<CloudUpload />}
                onClick={() => fileInputRef.current?.click()}
                loading={uploadCoverImageMutation.isPending}
              >
                Upload Custom Image
              </Button>
              {opportunityPhotos.length > 0 && (
                <Button
                  variant="outlined"
                  color="primary"
                  startDecorator={<ImageIcon />}
                  onClick={() => setShowPhotoPicker(!showPhotoPicker)}
                >
                  {showPhotoPicker ? 'Hide Photos' : `Use a project photo (${opportunityPhotos.length})`}
                </Button>
              )}
              {isCustomImage && (
                <Button
                  variant="outlined"
                  color="danger"
                  onClick={() => removeCoverImageMutation.mutate()}
                  loading={removeCoverImageMutation.isPending}
                >
                  Remove Custom Image
                </Button>
              )}
            </Stack>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              hidden
              accept="image/*"
            />

            {/* Photo Picker Grid */}
            {showPhotoPicker && (
              <Box sx={{ mt: 2 }}>
                <Typography level="body-sm" sx={{ mb: 1, fontWeight: 'md' }}>
                  Choose a proposal header. These photos are already attached to this opportunity:
                </Typography>
                {isLoadingPhotos ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size="sm" />
                  </Box>
                ) : opportunityPhotos.length === 0 ? (
                  <Typography level="body-sm" sx={{ color: 'text.tertiary', py: 2 }}>
                    No photos found for this opportunity.
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                      gap: 1,
                      maxHeight: 300,
                      overflowY: 'auto',
                      p: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 'sm',
                    }}
                  >
                    {opportunityPhotos.map((photo: any) => (
                      <Box
                        component="button"
                        type="button"
                        aria-label={`Use ${photo.description || 'roof photo'} as proposal header`}
                        key={photo.id}
                        onClick={() => setCoverFromPhotoMutation.mutate(photo.id)}
                        sx={{
                          position: 'relative',
                          aspectRatio: '1',
                          cursor: 'pointer',
                          borderRadius: 'sm',
                          overflow: 'hidden',
                          border: '2px solid transparent',
                          transition: 'all 0.2s',
                          opacity: setCoverFromPhotoMutation.isPending ? 0.5 : 1,
                          '&:hover': {
                            borderColor: 'primary.500',
                            transform: 'scale(1.02)',
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={photo.thumbnail_url || photo.image_url}
                          alt={photo.description || 'Photo'}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                        {photo.description && (
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              bgcolor: 'rgba(0,0,0,0.6)',
                              px: 0.5,
                              py: 0.25,
                            }}
                          >
                            <Typography
                              level="body-xs"
                              sx={{
                                color: 'white',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {photo.description}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 1 }}>
              Recommended size: 800x600 pixels. Max file size: 5MB. Supported formats: JPEG, PNG, WebP.
            </Typography>
          </CardContent>
        </Card>

        {/* Featured Manufacturer */}
        <Card variant="outlined">
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Featured Manufacturer
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
              Select a manufacturer to feature their marketing materials and populate the
              {' '}{tierBlurb} pricing tiers in this proposal
            </Typography>
            <FormControl>
              <FormLabel>Manufacturer</FormLabel>
              <Select
                placeholder="Select manufacturer..."
                value={selectedManufacturerId}
                onChange={(_, value) => onFieldChange('featured_manufacturer', value)}
              >
                <Option value={null}>None</Option>
                {manufacturers.map((mfr) => (
                  <Option key={mfr.id} value={mfr.id}>
                    {mfr.name}
                  </Option>
                ))}
              </Select>
            </FormControl>
            <Typography level="body-xs" sx={{ mt: 1, color: 'text.secondary' }}>
              Choosing None removes the manufacturer pages and keeps your existing package products and prices.
            </Typography>
            {selectedManufacturerId != null && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 1 }}>
                  The selected manufacturer's marketing materials will be available to add as pages
                  in this proposal. Saving a different manufacturer refreshes the {tierBlurb}
                  {' '}tier products from your Default Pricing — except tiers where you manually
                  picked a product.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Tax Settings */}
        <Card variant="outlined">
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Tax Settings
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
              Configure tax to be applied to all tier totals
            </Typography>
            <Stack spacing={2}>
              <FormControl>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Switch
                    checked={formData.tax_enabled ?? proposal.tax_enabled ?? false}
                    onChange={(e) => onFieldChange('tax_enabled', e.target.checked)}
                  />
                  <Typography level="body-md">Apply Tax to Pricing</Typography>
                </Stack>
              </FormControl>

              {(formData.tax_enabled ?? proposal.tax_enabled) && (
                <FormControl>
                  <FormLabel>Tax Rate (%)</FormLabel>
                  <Input
                    type="number"
                    value={formData.tax_rate ?? proposal.tax_rate ?? 0}
                    onChange={(e) => onFieldChange('tax_rate', parseFloat(e.target.value) || 0)}
                    placeholder="e.g., 8.25"
                    sx={{ maxWidth: 150 }}
                    endDecorator={<Typography level="body-sm">%</Typography>}
                  />
                </FormControl>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Introduction */}
        <Card variant="outlined">
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Introduction Letter
            </Typography>
            <FormControl>
              <FormLabel>Introduction Text</FormLabel>
              <Textarea
                minRows={4}
                value={formData.introduction_text || proposal.introduction_text}
                onChange={(e) => onFieldChange('introduction_text', e.target.value)}
                placeholder="Dear customer, thank you for considering us for your roofing project..."
              />
            </FormControl>
          </CardContent>
        </Card>

        {/* Scope of Work */}
        <Card variant="outlined">
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Project scope
            </Typography>
            <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
              Describe the work that applies to every option, such as the roof areas, preparation, and cleanup.
              Use each package’s included-work items for differences between Good, Better, and Best.
              Project scope is a description; it does not add charges.
            </Typography>
            <Stack spacing={1}>
              {(formData.scope_of_work || proposal.scope_of_work || []).map((item, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1 }}>
                  <Input
                    size="sm"
                    value={item}
                    onChange={(e) => {
                      const newScope = [...(formData.scope_of_work || proposal.scope_of_work || [])];
                      newScope[index] = e.target.value;
                      onFieldChange('scope_of_work', newScope);
                    }}
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="danger"
                    onClick={() => {
                      const newScope = (formData.scope_of_work || proposal.scope_of_work || []).filter(
                        (_, i) => i !== index
                      );
                      onFieldChange('scope_of_work', newScope);
                    }}
                  >
                    <Delete size={18} />
                  </IconButton>
                </Box>
              ))}
              <Button
                size="sm"
                variant="plain"
                startDecorator={<Add />}
                onClick={() => {
                  const newScope = [...(formData.scope_of_work || proposal.scope_of_work || []), ''];
                  onFieldChange('scope_of_work', newScope);
                }}
              >
                Add Scope Item
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Terms & Conditions */}
        <Card variant="outlined">
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Terms & Conditions
            </Typography>
            <FormControl>
              <Textarea
                minRows={6}
                value={formData.terms_and_conditions || proposal.terms_and_conditions}
                onChange={(e) => onFieldChange('terms_and_conditions', e.target.value)}
                placeholder="Enter your terms and conditions..."
              />
            </FormControl>
          </CardContent>
        </Card>

        {/* Internal Notes */}
        <Card variant="outlined">
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Internal Notes
            </Typography>
            <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 1 }}>
              These notes are not visible to the customer
            </Typography>
            <FormControl>
              <Textarea
                minRows={3}
                value={formData.notes || proposal.notes}
                onChange={(e) => onFieldChange('notes', e.target.value)}
                placeholder="Internal notes..."
              />
            </FormControl>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};
